/**
 *
 * @module contentApi
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/content.md
 * @see https://github.bamtech.co/sdk-distribution/bam-sdk/blob/master/Features/ContentApi.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import ContentManager from './contentManager';
import SearchOverrides from './searchOverrides';
import ContentQuery from './contentQuery';

const Util = Services.Util;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const ContentApiDustUrnReference = Services.Internal.Dust.DustUrnReference.content.contentApi;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class ContentApi
 * @desc Provides a way to access and discover content.
 *
 */
export default class ContentApi {

    /**
     *
     * @param {ContentManager} contentManager
     * @param {SDK.Logging.Logger} [logger=Logging]
     *
     */
    constructor(contentManager, logger = Logging) {

        const errorMessage = {
            ctor: 'ContentApi(contentManager, logger)',
            get contentManager() {
                return `${this.ctor} needs ContentManager instance`;
            }
        };

        instanceStrict(contentManager, ContentManager, errorMessage.contentManager, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {ContentManager}
         *
         */
        this.contentManager = contentManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.contentManager.client.dustEnabled);

        this.logger.info(ContentApi.name, 'Created.');

    }

    /**
     *
     * @access public
     * @param {Object} options
     * @param {ContentQuery} options.query - The GraphQL query object that defines the query criteria.
     * @param {SDK.Content.SearchOverrides} [options.overrides] - Object which contains override information on the time
     * of origin and or the country of origin to apply to the query request
     * @param {String} [options.contentTransactionId] - An application developer provided value to be included with the
     * HTTP request as the value of the `X-Content-Transaction-ID` header
     * @desc Queries the content services.
     * @throws {AuthenticationExpiredException} Authentication has expired and needs to be updated before proceeding.
     * @note Request to exchange token returns HTTP 400 with error: invalid_grant and error_description: expired-token.
     * @throws {LocationNotAllowedException} User is not allowed to use the app in their current location.
     * Some products prohibit the use of the app in certain locations.
     * @note Request to exchange token returns HTTP 400 with error: unauthorized_client and
     * error_description: unknown-location, unreliable-location, or forbidden-location.
     * @throws {LocationAcquisitionException} Unable to get user location due to location service being disabled.
     * @note Only applicable on certain platforms.
     * @note GeoProvider throws exception while trying to acquire location.
     * @throws {InvalidRequestException} The content query request was invalid.
     * @note Content query request returns HTTP 400. Response body is ignored.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Object>} A promise that completes when the operation has
     * succeeded and returns the JSON object or string to be interpreted by the application.
     *
     */
    async query(options) {

        const errorMessage = {
            method: 'ContentApi.query(options)',
            get options() {
                return `${this.method} options should be a non-empty Object`;
            },
            get query() {
                return `${this.method} options.query should be an instance of SDK.Content.ContentQuery`;
            },
            get overrides() {
                return `${this.method} options.overrides should be an instance of SDK.Content.SearchOverrides`;
            },
            get contentTransactionId() {
                return `${this.method} options.contentTransactionId should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { query, overrides, contentTransactionId } = options;

        instanceStrict(query, ContentQuery, errorMessage.query, InvalidArgumentException);

        if (Check.assigned(overrides)) {
            instanceStrict(overrides, SearchOverrides, errorMessage.overrides, InvalidArgumentException);
        }

        if (Check.assigned(contentTransactionId)) {
            nonEmptyString(contentTransactionId, errorMessage.contentTransactionId, InvalidArgumentException);
        }

        const { contentManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: ContentApi.name,
            urn: ContentApiDustUrnReference.query,
            data: {
                context: query.context,
                contentTransactionId
            }
        });

        try {

            return await contentManager.query(options);

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /**
     *
     * @access public
     * @param {String} suggestionSet - The predefined dictionary name to search.
     * @param {String} query - The predefined name of the search query.
     * @param {Number} limit - The maximum number of items that can be returned.
     * @param {String} filter - The string filter that should be applied to the query.
     * @desc Gets a set of search suggestions based on end-user input. Useful for type-ahead feature.
     * @note Alternatively, the app dev may choose to perform a query
     * instead to get a more rich set of search results as the user types.
     * @throws {TemporarilyUnavailableException} Server is too busy and rejected request.
     * @note Any request returns HTTP 5xx.
     * @throws {NetworkException} Internet connection or timeout error.
     * @note Unable to contact service for any reason (no server response received).
     * @throws {ServiceException} An unexpected error has occurred.
     * @note Unexpected response from server. This could be an unexpected HTTP code or invalid or
     * missing response data. Most likely indicates a bug in the services or SDK.
     * @returns {Promise<Array>} A promise that completes when the operation has succeeded and
     * returns an array of string search suggestions to allow the end-user to choose from.
     *
     */
    async getSearchSuggestions(suggestionSet, query, limit, filter) {

        const { contentManager, dustEnabled, logger } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: ContentApi.name,
            urn: ContentApiDustUrnReference.getSearchSuggestions
        });

        try {

            return await contentManager.getSearchSuggestions(suggestionSet, query, limit, filter);

        } catch (ex) {

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            return Promise.reject(ex);

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

}



// WEBPACK FOOTER //
// ./src/content/contentApi.js